import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AddedProvider, AddProviders, BusinessType, RemoveProviders, Tenant } from '@core/model';
import { ValidateTaxModalComponent } from '@feature/home/select-account/validate-tax-modal/validate-tax-modal.component';
import { ProviderAccountDTO } from '@mynexus/mynexus-ui-lib';
import { ProviderTypes } from '@shared/enum/provider-types';
import { TenantNames } from '@mynexus/mynexus-ui-lib';
import { ConfirmationService, MessageService, SelectItem } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Popover } from 'primeng/popover';
import { FormControl, FormGroup } from '@angular/forms';
import { RecaptchaComponent } from '@shared/recaptcha-v2/recaptcha.component';

@Component({
    selector: 'app-select-account',
    templateUrl: './select-account.component.html',
    styleUrls: ['./select-account.component.scss'],
    providers: [DialogService, ConfirmationService],
    standalone: false
})
export class SelectAccountComponent implements OnInit, OnChanges {
  @ViewChild(RecaptchaComponent) captcha!: RecaptchaComponent;
 
  @Input() userInfoForm!: FormGroup;
  @Input() tenants: Tenant[] = [];

  @Input() providerType?: BusinessType = null;

  @Input() selectedTenants: Tenant[] = [];

  @Input() providers: AddedProvider;

  @Input() providerTypes: BusinessType[];
  
  @Input() set emailServerError(value: 'emailAlreadyExists' | null) {
    this.emailExists = value === 'emailAlreadyExists';
  }

  @Output() save: EventEmitter<any> = new EventEmitter<any>();

  @Output() goBack: EventEmitter<any> = new EventEmitter<any>();

  @Output() addProvider: EventEmitter<AddProviders> = new EventEmitter<AddProviders>();

  @Output() deleteProviders: EventEmitter<RemoveProviders> = new EventEmitter<RemoveProviders>();

  @Output() addAccount: EventEmitter<Tenant> = new EventEmitter<Tenant>();

  @Output() deleteAccount: EventEmitter<Tenant> = new EventEmitter<Tenant>();

  @Output() changeProviderType: EventEmitter<BusinessType> = new EventEmitter<BusinessType>();

  @Output() recaptchaTokenChanged = new EventEmitter<string>();

  @ViewChild('op') tenantSelectDropdown: Popover;

  providerTypeOptions: SelectItem<BusinessType>[] = [];
  availableTenants: Tenant[] = [];
  selectedProviders: AddedProvider = null;
  selectedTenant: Tenant;
  selectedProvider: BusinessType;
  providerTypesList: BusinessType[] = [];
  ref: DynamicDialogRef;
  isCaptchaResolved = false;
  isProviderAdded = false;
  hasSingleHealthPlan = false;
  emailExists: boolean = false;

  tenantsByProviderTypes = new Map<string, TenantNames[]>();
  providersTypesByTenants = new Map<TenantNames, ProviderTypes[]>([
    [
      TenantNames.Anthem,
      [
        ProviderTypes.DMEPOSProvider,
        ProviderTypes.HealthPlanCaseManager,
        ProviderTypes.HHAdmin,
        ProviderTypes.HHAgency,
        ProviderTypes.HHBillingOffice,
        ProviderTypes.HospitalPlanner,
        ProviderTypes.Physician,
        ProviderTypes.PostAcuteFacility,
        ProviderTypes.SDoHClinician,
        ProviderTypes.SDoHProviderAdmin,
        ProviderTypes.SpecialtyProviderAdmin,
        ProviderTypes.SpecialtyClinician,
      ]
    ],
    [
      TenantNames.Aetna,
      [
        ProviderTypes.HealthPlanCaseManager,
        ProviderTypes.HHAdmin,
        ProviderTypes.HHAgency,
        ProviderTypes.HHBillingOffice,
        ProviderTypes.Physician
      ]
    ],
    [
      TenantNames.BCBSRI,
      [
        ProviderTypes.HealthPlanCaseManager,
        ProviderTypes.HospitalPlanner,
        ProviderTypes.Physician,
        ProviderTypes.PostAcuteFacility
      ]
    ],
    [
      TenantNames.Regence,
      [
        ProviderTypes.HealthPlanCaseManager,
        ProviderTypes.HHAgency,
        ProviderTypes.HospitalPlanner,
        ProviderTypes.Physician,
        ProviderTypes.PostAcuteFacility
      ]
    ],
    [
      TenantNames.Wellpoint,
      [
        ProviderTypes.HealthPlanCaseManager,
        ProviderTypes.HospitalPlanner,
        ProviderTypes.Physician,
        ProviderTypes.PostAcuteFacility
      ]
    ]
  ]);

  singleProviderTypes = [
    ProviderTypes.HealthPlanCaseManager,
    ProviderTypes.Physician,
    ProviderTypes.HospitalPlanner // Will be allowed to have multiple tenants in the future: US 194285
  ]

  restrictedTenants = [
    // Humana and UnitedHealthCare are not used anymore
    TenantNames.Humana,
    TenantNames.UnitedHealthcare,
  ];

  restrictedProviderTypes = [
    ProviderTypes.CHAPPProvider, // Removed per US 216419
    ProviderTypes.WCCClinician,
    ProviderTypes.WCCProviderAdmin
  ];

  get isHHProviderType(): boolean {
    return (
      this.providerType?.businessType === ProviderTypes.HealthPlanCaseManager ||
      this.providerType?.businessType === ProviderTypes.Physician ||
      this.providerType?.businessType === ProviderTypes.HospitalPlanner
    );
  }

  get canSubmit(): boolean {
    const isFormsResolved = this.isCaptchaResolved && this.providerType;

    const isEmailValid =
      this.userInfoForm?.get('preferredBusinessEmail')?.valid;

    return this.isHHProviderType
      ? isFormsResolved && !!this.selectedTenants.length && isEmailValid
      : isFormsResolved && this.hasAddedProviders() && isEmailValid;
  }

  constructor(
    public dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.createProviderTypeOptions();
    this.createTenantsByProvider();
  }

  ngOnChanges(event: SimpleChanges) {

    if (event.providerTypes?.currentValue) {
      const providerTypes = event.providerTypes?.currentValue;

      this.providerTypesList = providerTypes;
      this.createProviderTypeOptions();
      
      this.setAvailableTenants();
      this.hideTenant(this.providerType);
    }

    if (event.providerType?.currentValue) {
      this.hideTenant(event.providerType?.currentValue);
    }
    if (event.providers?.currentValue) {
      const providers = event.providers?.currentValue;

      this.setAvailableTenants();
      this.hideTenant(this.providerType);
      this.selectedProviders = providers;
      this.isProviderAdded = Object.values(providers).some(provider => Object.keys(provider).length !== 0);
    }

    if (event.selectedTenants?.currentValue) {
      this.selectedTenants.forEach(tenant => {
        this.buildProviderTypesList(tenant);
      });
    }

    if (event.providerType?.currentValue && Object.keys(event.providerType?.currentValue).length === 0) {
      this.hasSingleHealthPlan = false;
    }
  }
  
  get emailCtrl(): FormControl {
    return this.userInfoForm.get('preferredBusinessEmail') as FormControl;
  }

  addTenant(event: MouseEvent, tenant: Tenant) {
    event.preventDefault();

    this.selectedTenant = tenant;
    this.checkAccountMultiselect();
    this.tenantSelectDropdown.hide();

    if (tenant) {
      this.addAccount.emit(tenant);
    }
  }

  handleAddProvider(providerAccounts: ProviderAccountDTO[], tenant: Tenant) {
    this.ref = this.dialogService.open(ValidateTaxModalComponent, {
      header: 'Enter NPI and Tax ID for the selected provider.',
      style: { width: '400px' },
      modal: true,
      draggable: false,
      closable: true,
      data: {
        providers: providerAccounts,
        tenant
      }
    });

    this.ref.onClose.subscribe(providers => {
      if (providers) {
        this.addProvider.emit({ providers, tenant });
        this.isProviderAdded = true;
      }
    });
  }

  handleRemoveProviders(providers, tenant: Tenant) {
    this.deleteProviders.emit({ tenant, providers });
    this.isProviderAdded = true;
  }

  handleChangeProviderType({ value: providerType }: { value: BusinessType | null }) {
    this.selectedProvider = providerType;
    this.setAvailableTenants();
    this.hideTenant(providerType);
    this.changeProviderType.emit(providerType);
  }

  handleDeleteAccount(tenant: Tenant) {
    this.deleteAccount.emit(tenant);
    this.isProviderAdded = false;
    this.hasSingleHealthPlan = false;
    this.selectedTenants = this.selectedTenants.filter(x => x !== tenant);
  }

  handleCaptchaResolve($event) {
    this.isCaptchaResolved = Boolean($event);
    this.recaptchaTokenChanged.emit($event);
  }

  handleSubmit() {
    if (this.selectedTenant?.name === 'Regence' && this.selectedProvider?.businessType === 'Health Plan Case Manager') {
      this.messageService.add({
        severity: 'error',
        summary: 'Unable to Register with Selected Health Plan and Account Type',
        key: 'error',
        detail: 'If you are a Regence Health Plan Case Manager, you must register using the Single Sign-On (SSO) process.',
        life: 5000
      });
      return;
    }
    // No providers needed for HH type. We can skip confirmation and save
    if (this.isHHProviderType || this.providerType?.businessType === 'Post-Acute Facility') {
      return this.save.emit();
    }

    this.confirmationService.confirm({
      message: 'Do you need to add Providers associated with a different Health Plan?',
      rejectButtonStyleClass: 'p-button-secondary',
      reject: () => {
        this.save.emit();
      }
    });
  }

  private hasAddedProviders() {
    return Object.values(this.selectedProviders).some(a => Object.keys(a).length > 0);
  }

  private createProviderTypeOptions() {
    this.providerTypeOptions = [
      { label: 'Select Provider Type', value: null },
      ...this.providerTypesList
      .filter(t => !this.restrictedProviderTypes.some(provider => t.businessType === provider))
      .map(t => ({
        value: t,
        label: t.displayName || t.businessType
      }))
    ];
  }

  private buildProviderTypesList(tenant: Tenant): void {
    this.filterProviderTypes(tenant);

    if (this.selectedTenants.length > 1) {
      this.providerTypesList = this.providerTypesList.filter(
        e => !this.singleProviderTypes.includes(e.businessType as ProviderTypes)
      );
    }

    this.createProviderTypeOptions();
  }


  private filterProviderTypes(tenant: Tenant): void {
    const eligibleProviders = this.providersTypesByTenants.get(tenant.name as TenantNames);
    this.providerTypesList = this.providerTypesList.filter(e =>
      eligibleProviders.some(provider => e.businessType === provider)
    );
  }

  private hideTenant(providerType: BusinessType): void {
    if (!this.selectedProvider) {
      this.availableTenants = this.tenants;
      this.setAvailableTenants();
    } else if (providerType) {
      const eligibleTenants = this.tenantsByProviderTypes.get(providerType.businessType);
      const tenantList = this.tenants.filter(tenant => eligibleTenants?.includes(tenant.name as TenantNames));

      const filtered = tenantList.filter(tenant =>
        !this.selectedTenants.some(selectedTenant => selectedTenant.name === tenant.name)
      );
      this.availableTenants = filtered;
    }

    const isSingleProviderType = this.singleProviderTypes.includes(providerType?.businessType as ProviderTypes);
    if (isSingleProviderType && this.selectedTenant) {
      this.hasSingleHealthPlan = true;
    } else {
      this.hasSingleHealthPlan = false;
    }

    if (this.selectedTenants.length === 0) {
      this.hasSingleHealthPlan = false;
    }
  }

  private setAvailableTenants(): void {
    this.availableTenants = this.tenants.filter(tenant => !this.restrictedTenants.includes(tenant.name as TenantNames));

    // Prevent duplicate tenants from being added
    if (this.selectedTenants.length > 0) {
      this.availableTenants = this.availableTenants.filter(tenant => !this.selectedTenants.includes(tenant));
    }
  }

  private checkAccountMultiselect() {
    const isSingleSelectProviderType = this.singleProviderTypes.includes(this.selectedProvider?.businessType as ProviderTypes);
    if (this.selectedTenant && isSingleSelectProviderType) {
      this.hasSingleHealthPlan = true;
    }
  }

  private createTenantsByProvider(): void {
    for (const [tenant, providerList] of this.providersTypesByTenants) {
      for (const provider of providerList) {
        if (!this.tenantsByProviderTypes.has(provider)) {
          this.tenantsByProviderTypes.set(provider, []);
        }
        this.tenantsByProviderTypes.get(provider).push(tenant);
      }
    }
  }
}
